/* eslint-disable react/no-unescaped-entities --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { Eye, Shield } from "lucide-react";
import { toast } from "sonner";

export default function MediatorSignup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mediator/register", { name, email, password, code });
      setToken(data.token);
      // v77.49 — Helpful message when an existing third-party
      // account just gained a new client. The backend returns
      // `added_grant_for_household` only on the existing-account
      // path; on a brand-new registration that field is absent.
      if (data.added_grant_for_household) {
        toast.success("New client added to your account");
      } else {
        toast.success("Access granted");
      }
      // v77.49 — Mediator users land on the dedicated third-party
      // dashboard, not the parent dashboard.
      navigate("/app/third-party");
      // Force reload to pick up role
      setTimeout(() => window.location.reload(), 200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid invite");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F7F8] flex flex-col">
      <header className="border-b border-[#D9E1E3] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="mediator-brand">
            <Logo size={32} />
            <span className="font-heading font-semibold text-lg">CoParent Console</span>
          </Link>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md border border-[#D9E1E3] bg-white rounded-md p-6 md:p-8" data-testid="mediator-card">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-[#1F9FA4] bg-[#D6F0EF] text-[#116B6E] rounded-sm text-xs uppercase tracking-[0.1em]">
            <Eye className="w-3 h-3" /> View-only access
          </div>
          <h1 className="font-heading text-2xl font-semibold mt-4">Mediator / solicitor sign-up</h1>
          <p className="text-sm text-[#6F828A] mt-2">Create your view-only account using the invite code shared with you. You can read the records the parent has shared, including messages, calendar, expenses, documents and shared journal entries. You cannot post, edit or access private journal entries.</p>
          <p className="text-xs text-[#3F595B] bg-[#E0F2F2] border border-[#A6D7D9] rounded-sm px-3 py-2 mt-3" data-testid="mediator-existing-hint">
            <strong>Already have an account?</strong> Use the <span className="font-mono-data">same email and password</span> you set previously. We'll add this new client to your existing dashboard — no duplicate account needed.
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="m-code">Invite code</Label>
              <Input id="m-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required className="font-mono-data tracking-widest" data-testid="mediator-code-input" />
            </div>
            <div>
              <Label htmlFor="m-name">Full name</Label>
              <Input id="m-name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="mediator-name-input" />
            </div>
            <div>
              <Label htmlFor="m-email">Email</Label>
              <Input id="m-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="mediator-email-input" />
            </div>
            <div>
              <Label htmlFor="m-pw">Password</Label>
              <PasswordInput id="m-pw" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="mediator-password-input" />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-[#1F9FA4] hover:bg-[#178085] rounded-sm" data-testid="mediator-submit-button">
              {busy ? "Creating access…" : "Create view-only account"}
            </Button>
            <div className="text-xs text-[#6F828A] flex items-start gap-2 pt-1">
              <Shield className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Access is time-limited and may be revoked by the household at any time.
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
